import { AlertCircle } from '@untitled-ui/icons-react';
import { type InputHTMLAttributes, type ReactNode, type Ref, forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { normalizeErrors } from '../../error/utils';
import { ErrorField, type ErrorFieldProps } from './error-field';

export type TextFieldProps = InputHTMLAttributes<HTMLInputElement> & {
  className?: string;
  sizeField?: 'xs' | 'sm' | 'md' | 'lg';
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  error?: ErrorFieldProps['error'];
  delegatedErrorMessage?: boolean;
  overrideErrorIcon?: boolean;
  description?: string;
  label?: string | ReactNode;
  isNew?: boolean;
  containerClassName?: string;
};

const notInputProps = [
  'icon',
  'sizeField',
  'iconPosition',
  'error',
  'hideErrorText',
  'delegatedErrorMessage',
];

const inputSizeClassNames = {
  xs: 'px-2.5 py-1.5 h-8',
  sm: 'px-3.5 py-2 h-9',
  md: 'px-4 py-2.5 h-10',
  lg: 'px-4.5 py-2.5 h-11',
};

const inputFontSizeClassNames = {
  xs: 'font-body-2-m',
  sm: 'font-body-2-m',
  md: 'font-body-2-m',
  lg: 'font-body-1-m',
};

const secondaryFontSizeClassNames = {
  xs: 'font-body-3-r',
  sm: 'font-body-3-r',
  md: 'font-body-2-r',
  lg: 'font-body-2-r',
};

export const TextField = forwardRef(function TextField(
  props: TextFieldProps,
  ref: Ref<HTMLInputElement>
) {
  const {
    className = '',
    iconLeft,
    iconRight,
    isNew = false,
    sizeField = 'sm',
    error,
    overrideErrorIcon = false,
    description,
    label,
    delegatedErrorMessage = false,
    containerClassName,
    ...inputProps
  } = props;
  for (let i = 0; i < notInputProps.length; i += 1) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    delete (inputProps as any)[notInputProps[i]];
  }

  const errors = normalizeErrors(error);
  const inputClassNames = twMerge([
    'w-full bg-white outline-none focus:border-branding-accent border-branding-gray disabled:text-branding-gray font-body-2-m text-black placeholder:text-branding-gray disabled:bg-branding-gray-50',
    inputFontSizeClassNames[sizeField],
    isNew ? 'bg-branding-accent-50 px-2' : '',
    className,
  ]);
  const inputContainerClassName = twMerge(
    'rounded-md bg-white border border-branding-gray has-[:focus]:border-[1.5px] has-[:focus]:border-branding-accent relative flex items-center has-[:disabled]:bg-branding-gray-50 shadow-1 has-[:focus]:shadow-accent',
    inputSizeClassNames[sizeField],
    errors.length > 0
      ? 'has-[:focus]:shadow-error has-[:focus]:border-red-500 border-red-500'
      : 'border-branding-gray',
    isNew ? 'px-2' : '',
    containerClassName
  );
  const iconRightRender =
    errors.length > 0 && !overrideErrorIcon ? (
      <AlertCircle width={16} height={16} className="text-red-500" />
    ) : (
      iconRight
    );
  const textInput = (
    <div
      className={inputContainerClassName}
      onClick={function () {
        if (ref !== null && typeof ref === 'object' && 'current' in ref && ref.current !== null) {
          ref.current.focus();
        }
      }}
    >
      {iconLeft != null ? <div className="pr-2 text-gray">{iconLeft}</div> : null}
      <input type="text" className={inputClassNames} {...inputProps} ref={ref} />
      {iconRightRender != null ? <div className="pl-2 text-gray">{iconRightRender}</div> : null}
    </div>
  );
  const descriptionRender =
    description == null ? null : (
      <p className={twMerge(secondaryFontSizeClassNames[sizeField], 'text-branding-gray')}>
        {description}
      </p>
    );

  return (
    <div className="flex w-full flex-col gap-1">
      {label == null ? (
        textInput
      ) : (
        <label>
          <div className={twMerge('mb-1.5 text-black', secondaryFontSizeClassNames[sizeField])}>
            {label}
          </div>
          {textInput}
        </label>
      )}
      {errors.length > 0 && !delegatedErrorMessage ? (
        <ErrorField error={error} sizeField={sizeField} />
      ) : (
        descriptionRender
      )}
    </div>
  );
});
